import { visibleActionCount } from '@components/RingilDataTable/v3/bulkActionsLayout';

export interface TabOverflowLayout {
  /** Indices of the tabs shown in the strip, in their original order. */
  visible: number[];
  /** Indices of the tabs folded into the "…" menu, in their original order. */
  folded: number[];
}

/**
 * Which tabs of a strip are shown and which fold into a "…" menu.
 *
 * All of them when they fit. Otherwise as many leading tabs as fit beside the "…" button — except that the
 * active tab is never folded: when it would be, it takes the last slot of the strip and the tabs before it
 * give up as much room as it needs. So scrolling (or clicking) into a folded section pulls its tab out of
 * the menu, and the neighbours fold to make way.
 *
 * `activeIndex` outside the tabs (-1, a trigger-only tab like Messages that is never active) pins nothing.
 */
export const tabOverflowLayout = (widths: number[], available: number, gap: number, moreButtonWidth: number, activeIndex: number = -1): TabOverflowLayout => {
  const count = visibleActionCount(widths, available, gap, moreButtonWidth);
  const hasActive = activeIndex >= 0 && activeIndex < widths.length;

  let visible: number[];
  if (!hasActive || activeIndex < count) {
    visible = widths.slice(0, count).map((_, i) => i);
  } else {
    // The active tab and the "…" button always stay, even where that alone overflows a very narrow pane.
    let used = moreButtonWidth + gap + widths[activeIndex];
    visible = [];
    for (let i = 0; i < activeIndex; i++) {
      const next = used + gap + widths[i];
      if (next > available) break;
      used = next;
      visible.push(i);
    }
    visible.push(activeIndex);
  }

  const shown = new Set(visible);
  return { visible, folded: widths.map((_, i) => i).filter(i => !shown.has(i)) };
};
